import React, { useCallback } from "react";
import styled from "styled-components";
import { Chain } from "@sablier/v2-components/molecules";
import { tags } from "@sablier/v2-constants";
import useModalChainPicker from "@sablier/v2-hooks/modals/useModalChainPicker";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    width: 100%;
    padding: ${(props) => props.theme.sizes.modalEdge};
  }
`;
const Connected = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    :before {
      position: relative;
      content: "";
      flex-shrink: 0;
      width: 8px;
      height: 8px;
      margin-right: 6px;
      border: 2px solid ${(props) => props.theme.colors.primaryMiddle};
      border-radius: 50%;
    }
  }

  p {
    color: ${(props) => props.theme.colors.primaryMiddle};
    font-weight: 700;
    font-size: 11pt;
  }
`;

const Item = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    border: 2px solid ${(props) => props.theme.colors.transparent};
    border-radius: 8px;

    & > *:first-child {
      flex: 1;
    }

    &[data-active="true"] {
      order: 0;
      border: 2px solid ${(props) => props.theme.colors.border};
      background-color: ${(props) => props.theme.colors.dark200};
      pointer-events: none;
    }

    &:not([data-active="true"]) {
      order: 1;
      &:hover,
      &:active {
        border: 2px solid ${(props) => props.theme.colors.border};
        background-color: ${(props) => props.theme.colors.border};
        cursor: pointer;
      }
    }
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
  }
  ${(props) => props.theme.medias.maxSM} {
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
  }
`;

function Body({ id: modalId }: { id: string }) {
  const { data } = useModalChainPicker(modalId);
  const { chainId, chains, onSelect } = data;
  const { t } = useT();

  const onClick = useCallback(
    (chainId: number) => {
      if (_.isFunction(onSelect)) {
        onSelect(chainId);
      }
    },
    [onSelect],
  );

  return (
    <Wrapper>
      {chains.map((item) => {
        const active = chainId === item.id;
        return (
          <Item
            data-active={active}
            key={item.id}
            onClick={() => onClick(item.id)}
          >
            <Chain id={item.id} size={28} cy={tags.modalChainPicker} />
            {active && (
              <Connected>
                <p>{_.capitalize(t("words.selected"))}</p>
              </Connected>
            )}
          </Item>
        );
      })}
    </Wrapper>
  );
}

export default Body;
